import type {
  LabelPrintFormat,
  LabelPrintMarginSettings,
  LabelPrinterProfile,
  LabelPrintRotation,
  PrinterModelPreset,
  ProductLabelData,
} from "@itaas/label-print";

const DEFAULT_BRIDGE_PORT = 9310;
const BRIDGE_BASE =
  import.meta.env.VITE_PRINT_BRIDGE_URL ?? `http://localhost:${DEFAULT_BRIDGE_PORT}`;

const PROBE_TIMEOUT_MS = 1500;
const PRINT_TIMEOUT_MS = 30000;
const FETCH_TIMEOUT_MS = 8000;

export interface PrintBridgeHealth {
  ok: boolean;
  service: string;
  version: string;
  platform: string;
}

export interface PrintBridgePrinter {
  name: string;
  driverName: string;
  portName: string;
  isDefault: boolean;
  paperSizes?: Array<{ name: string; widthMm: number; heightMm: number }>;
}

export interface PrintBridgePrintersResponse {
  printers: PrintBridgePrinter[];
  defaultPrinter: string | null;
  printerProfiles?: Record<string, LabelPrinterProfile>;
  platform: string;
}

export interface PrintBridgeConfigResponse {
  defaultPrinter?: string;
  printerProfiles?: Record<string, LabelPrinterProfile>;
}

export interface PrintBridgeFormatsResponse {
  formats: LabelPrintFormat[];
  groups: Array<{ id: string; label: string; formats?: LabelPrintFormat[] }>;
}

export interface PrintBridgeAgentInfo {
  version: string;
  port: number;
  installScriptUrl: string;
  platform: string;
}

let cachedBridgeHealth: PrintBridgeHealth | null | undefined;

async function bridgeFetch<T>(
  path: string,
  init?: RequestInit,
  timeoutMs = PROBE_TIMEOUT_MS,
): Promise<T> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`${BRIDGE_BASE}${path}`, {
      ...init,
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        ...(init?.headers ?? {}),
      },
    });
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { error?: string };
      throw new Error(body.error ?? `Print Bridge HTTP ${response.status}`);
    }
    return (await response.json()) as T;
  } finally {
    window.clearTimeout(timeout);
  }
}

export function getPrintBridgeBaseUrl(): string {
  return BRIDGE_BASE;
}

export async function probePrintBridge(force = false): Promise<PrintBridgeHealth | null> {
  if (!force && cachedBridgeHealth !== undefined) {
    return cachedBridgeHealth;
  }
  try {
    const health = await bridgeFetch<PrintBridgeHealth>("/health");
    cachedBridgeHealth = health.ok ? health : null;
    return cachedBridgeHealth;
  } catch {
    cachedBridgeHealth = null;
    return null;
  }
}

export function invalidatePrintBridgeCache(): void {
  cachedBridgeHealth = undefined;
}

export async function fetchPrintBridgePrinters(
  includePaperSizes = true,
): Promise<PrintBridgePrintersResponse> {
  const query = includePaperSizes ? "?paperSizes=1" : "";
  return bridgeFetch<PrintBridgePrintersResponse>(
    `/v1/printers${query}`,
    undefined,
    FETCH_TIMEOUT_MS,
  );
}

export async function fetchPrintBridgeConfig(): Promise<PrintBridgeConfigResponse> {
  return bridgeFetch<PrintBridgeConfigResponse>("/v1/config", undefined, FETCH_TIMEOUT_MS);
}

export async function fetchPrintBridgeFormats(): Promise<PrintBridgeFormatsResponse> {
  return bridgeFetch<PrintBridgeFormatsResponse>("/v1/formats", undefined, FETCH_TIMEOUT_MS);
}

export async function fetchPrinterModelPresets(): Promise<{ presets: PrinterModelPreset[] }> {
  return bridgeFetch<{ presets: PrinterModelPreset[] }>("/v1/presets", undefined, FETCH_TIMEOUT_MS);
}

export async function fetchPrintBridgeAgentInfo(): Promise<PrintBridgeAgentInfo | null> {
  try {
    return await bridgeFetch<PrintBridgeAgentInfo>("/v1/agent/info", undefined, FETCH_TIMEOUT_MS);
  } catch {
    return null;
  }
}

export function getPrintBridgeInstallScriptUrl(): string {
  return `${BRIDGE_BASE}/v1/agent/install.ps1`;
}

export async function savePrintBridgePrinterProfile(input: {
  printerName: string;
  formatId: string;
  rotation?: LabelPrintRotation;
  margins?: LabelPrintMarginSettings;
}): Promise<PrintBridgeConfigResponse> {
  return bridgeFetch<PrintBridgeConfigResponse>(
    "/v1/config",
    {
      method: "PATCH",
      body: JSON.stringify({
        printerProfile: input,
      }),
    },
    FETCH_TIMEOUT_MS,
  );
}

export async function setPrintBridgeDefaultPrinter(printerName: string): Promise<void> {
  await bridgeFetch(
    "/v1/config",
    {
      method: "PATCH",
      body: JSON.stringify({ defaultPrinter: printerName }),
    },
    FETCH_TIMEOUT_MS,
  );
}

export async function printLabelViaBridge(
  label: ProductLabelData,
  format: LabelPrintFormat,
  rotation: LabelPrintRotation = 0,
  printerName?: string,
  margins?: LabelPrintMarginSettings,
): Promise<{ printerName: string }> {
  const result = await bridgeFetch<{ ok: boolean; printerName: string }>(
    "/v1/print/label",
    {
      method: "POST",
      body: JSON.stringify({
        printerName,
        formatId: format.id,
        rotation,
        margins,
        label,
      }),
    },
    PRINT_TIMEOUT_MS,
  );
  return { printerName: result.printerName };
}

export const TEST_LABEL: ProductLabelData = {
  productName: "TEST ITaaS Hospitol",
  barcode: "TEST9310",
  sku: "VERIFY",
  quantity: 1,
  unit: "pc",
};

export async function printTestLabelViaBridge(
  format: LabelPrintFormat,
  rotation: LabelPrintRotation = 0,
  printerName?: string,
  margins?: LabelPrintMarginSettings,
): Promise<{ printerName: string }> {
  return printLabelViaBridge(TEST_LABEL, format, rotation, printerName, margins);
}
